<template>
  <div class="sea-calculator-edit">
    <div class="editor-header">
      <div class="header-title">
        <h2>
          <i class="bx bxs-ship"></i>
          Редактор калькулятора морских перевозок
        </h2>
        <div v-if="updatedAt" class="last-updated">
          <i class="bx bx-time"></i>
          Последнее обновление: {{ formatDate(updatedAt) }}
        </div>
      </div>
      <div class="editor-actions">
        <button 
          class="btn btn-success"
          @click="addSeaTariff"
        >
          <i class="bx bx-plus"></i>
          Добавить тариф
        </button>
        <button 
          class="btn btn-secondary"
          @click="$emit('close-editor')"
        >
          <i class="bx bx-x"></i>
          Закрыть редактор
        </button>
        <button 
          class="btn btn-primary"
          @click="saveCalculator"
          :disabled="isSaving"
        >
          <i class="bx bx-save"></i>
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>
    
    <div class="editor-content">
      <!-- Таблица тарифов морских перевозок -->
      <div class="calculator-section">
        <h3>
          <i class="bx bx-money"></i>
          Тарифы морских перевозок
        </h3>
        
        <div class="sea-table-container">
          <!-- Сообщение когда нет тарифов -->
          <div v-if="seaTariffs.length === 0" class="empty-tariffs-message">
            <i class="bx bx-info-circle"></i>
            <p>Пока нет данных о тарифах морских перевозок</p>
          </div>
          
          <!-- Таблица тарифов -->
          <table v-else class="sea-table">
            <thead>
              <tr>
                <th>LDM/m3</th>
                <th>Pll</th>
                <th>Вес (кг)</th>
                <th>Стоимость (EU)</th>
                <th style="text-align: center;">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(tariff, index) in seaTariffs" :key="index">
                <td>
                  <input 
                    v-model="tariff.ldm_m3" 
                    type="text" 
                    class="form-control"
                    placeholder="1m3"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="tariff.pll" 
                    type="number" 
                    class="form-control"
                    min="0"
                    placeholder="0"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="tariff.weight_kg" 
                    type="number" 
                    class="form-control"
                    step="0.01"
                    min="0"
                    placeholder="300"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="tariff.cost_eu" 
                    type="number" 
                    class="form-control"
                    step="0.01"
                    min="0"
                    placeholder="450"
                  />
                </td>
                <td style="text-align: center;">
                  <button 
                    v-if="!tariff.is_default"
                    class="btn btn-danger btn-sm"
                    @click="removeSeaTariff(index)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                  <span v-else class="text-muted">Системный</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_CALCULATOR_SETTINGS_SEA } from '@/constants'

export default {
  name: 'SeaCalculatorEdit',
  
  data() {
    return {
      isSaving: false,
      isLoading: true,
      seaTariffs: [],
      updatedAt: null
    }
  },
  
  async mounted() {
    await this.loadSettings()
  },
  
  methods: {
    async loadSettings() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_SEA)
        
        if (response.data.success && response.data.data) {
          this.seaTariffs = response.data.data.tariffs || []
          this.updatedAt = response.data.updated_at
        } else {
          this.seaTariffs = []
          this.updatedAt = null
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error)
        this.seaTariffs = []
        this.$toast.error('Ошибка загрузки настроек калькулятора')
      } finally {
        this.isLoading = false
      }
    },
    
    addSeaTariff() {
      this.seaTariffs.push({
        ldm_m3: '',
        pll: 0,
        weight_kg: 0,
        cost_eu: 0,
        is_default: false
      })
    },
    
    removeSeaTariff(index) {
      this.seaTariffs.splice(index, 1)
    },
    
    async saveCalculator() {
      try {
        this.isSaving = true
        
        const settings = {
          tariffs: this.seaTariffs
        }
        
        const response = await api.call('put', API_CALCULATOR_SETTINGS_SEA, { settings })
        
        if (response.data.success) {
          this.updatedAt = response.data.updated_at
          this.$toast.success('Калькулятор морских перевозок успешно сохранен')
        } else {
          this.$toast.error(response.data.message || 'Ошибка сохранения')
        }
      } catch (error) {
        console.error('Ошибка сохранения:', error)
        this.$toast.error('Ошибка сохранения настроек калькулятора')
      } finally {
        this.isSaving = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sea-calculator-edit {
  overflow: hidden;
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 15px 0px 30px 0;
    
    .header-title {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #0d6348;
        
        i {
          font-size: 24px;
        }
      }
      
      .last-updated {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #6c757d;
        font-style: italic;
        
        i {
          font-size: 14px;
        }
      }
    }
    
    .editor-actions {
      display: flex;
      gap: 8px;
      
      .btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 12px;
        
        i {
          font-size: 14px;
        }
        
        &.btn-primary {
          background: #e37926;
          color: white;
          border: 1px solid #d16a1a;
          
          &:hover:not(:disabled) {
            background: #d16a1a;
          }
          
          &:disabled {
            background: rgba(227, 121, 38, 0.6);
            cursor: not-allowed;
          }
        }
        
        &.btn-secondary {
          background: transparent;
          color: #0d6348;
          border: 1px solid #0d6348;
          
          &:hover {
            background: #0d6348;
            color: white;
          }
        }
      }
    }
  }
  
  .editor-content {
    padding: 20px;
    
    .calculator-section {
      margin-bottom: 30px;
      
      h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 15px 0;
        color: #0d6348;
        font-size: 18px;
        font-weight: 600;
        
        i {
          font-size: 20px;
        }
      }
      
      .sea-table-container {
        overflow-x: auto;
        margin-bottom: 20px;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        
        .empty-tariffs-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 6px;
          color: #6c757d;
          
          i {
            font-size: 36px;
            margin-bottom: 12px;
            color: #0d6348;
          }
          
          p {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
          }
        }
        
        .sea-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          min-width: 500px;
          
          th {
            background: #0d6348;
            color: white;
            font-weight: 600;
            padding: 8px 6px;
            text-align: left;
            border: 1px solid #0a4d3a;
            font-size: 11px;
            white-space: nowrap;
          }
          
          td {
            padding: 4px 6px;
            border: 1px solid #dee2e6;
            vertical-align: middle;
            
            .form-control {
              width: 100%;
              padding: 4px 6px;
              border: 1px solid #ced4da;
              border-radius: 3px;
              font-size: 11px;
              
              &:focus {
                outline: none;
                border-color: #0d6348;
                box-shadow: 0 0 0 2px rgba(13, 99, 72, 0.25);
              }
            }
            
            .btn {
              padding: 3px 6px;
              font-size: 11px;
              border-radius: 3px;
              
              &.btn-danger {
                background: #dc3545;
                color: white;
                border: none;
                
                &:hover {
                  background: #c82333;
                }
              }
            }
            
            .text-muted {
              color: #6c757d;
              font-size: 12px;
              font-style: italic;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .sea-calculator-edit {
    .editor-header {
      flex-direction: column;
      gap: 15px;
      text-align: center;
      
      .editor-actions {
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style>
